import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';

import {
  Close as CloseIcon,
  Schedule as TimeIcon,
  Event as EventIcon,
  Room as RoomIcon,
  Group as CapacityIcon,
  Description as DescriptionIcon,
  Label as TagIcon,
} from '@mui/icons-material';

import { Session } from '../../types';

interface SessionViewDialogProps {
  open: boolean;
  session: Session | null;
  onClose: () => void;
}

const SessionViewDialog: React.FC<SessionViewDialogProps> = ({
  open,
  session,
  onClose,
}) => {
  if (!session) return null;

  const formatTime = (time: string) => {
    return new Date(`1970-01-01T${time}`).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusStyle = session.isActive
    ? { bg: '#dcfce7', color: '#16a34a', text: 'Active' }
    : session.sessionStatus === 'completed'
      ? { bg: '#dbeafe', color: '#1d4ed8', text: 'Completed' }
      : { bg: '#fee2e2', color: '#dc2626', text: 'Inactive' };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          p: 3,
          pb: 2,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EventIcon sx={{ color: '#3b82f6', fontSize: 28 }} />
          <Box>
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 600 }}>
              Conference Session Details
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
              View session information
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{ color: '#6b7280', '&:hover': { bgcolor: '#f3f4f6' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Title & Status */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
                mt: 1,
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                {session.sessionTitle}
              </Typography>

              <Chip
                label={statusStyle.text}
                sx={{
                  bgcolor: statusStyle.bg,
                  color: statusStyle.color,
                  fontWeight: 600,
                }}
              />
            </Box>
          </Grid>

          {/* Session Type */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#f9fafb',
                border: '1px solid #e5e7eb',
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 1 }}>
                Session Type
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
                {session.sessionType}
              </Typography>
            </Box>
          </Grid>

          {/* Venue */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#f0f9ff',
                border: '1px solid #bae6fd',
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 1 }}>
                Venue
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
                {session.venue ? ` ${session.venue}` : 'Not Assigned'}
              </Typography>
            </Box>
          </Grid>

          {/* Date */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TimeIcon sx={{ color: '#6b7280', fontSize: 20, mt: 0.5 }} />
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1,
                  }}
                >
                  Schedule
                </Typography>

                <Typography sx={{ color: '#6b7280' }}>
                  <strong>Date: </strong>
                  {session.sessionDate}
                </Typography>

                <Typography sx={{ color: '#6b7280', mt: 0.5 }}>
                  <strong>Time: </strong>
                  {formatTime(session.startTime)} –{' '}
                  {formatTime(session.endTime)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Capacity */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                bgcolor: '#fefce8',
                borderRadius: 2,
                border: '1px solid #fef08a',
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', color: '#854d0e', mb: 1 }}>
                Capacity
              </Typography>
              <Typography
                sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#713f12' }}
              >
                {session.capacity}
              </Typography>
            </Box>
          </Grid>

          {/* Tags */}
          {session.sessionTags?.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {session.sessionTags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    icon={<TagIcon />}
                    sx={{
                      bgcolor: '#eef2ff',
                      color: '#4338ca',
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* Description */}
          {session.sessionDescription && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DescriptionIcon sx={{ mt: 0.5, color: '#6b7280' }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#374151',
                      mb: 1,
                    }}
                  >
                    Description
                  </Typography>
                  <Typography sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                    {session.sessionDescription}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          borderTop: '1px solid #e5e7eb',
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.3,
            textTransform: 'none',
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionViewDialog;
